import { useState, useEffect, useCallback } from 'react';
import { doctorShiftApi } from '../services/doctorShiftApiService';
import { doctorApiService } from '../services/doctorApiService';
import type {
  DoctorShift,
  DoctorShiftListState,
  DoctorShiftFilterOptions,
  DoctorShiftCreateRequest,
  DoctorShiftUpdateRequest
} from '../types/doctorShift';
import type { Doctor } from '../types/doctor';

/**
 * 医師シフト一覧管理用カスタムフック
 * 
 * 意図：
 * - 医師シフトデータの一覧取得・CRUD操作を統合管理
 * - フィルタリング・ソート・ページネーション機能
 * - モーダル・ダイアログの状態管理
 * 
 * 注意点：
 * - APIエラー時のリトライ処理
 * - メモリリークの防止
 * - 不要な再レンダリングの抑制
 * 
 * 改善提案：
 * - リアルタイム更新機能（WebSocket）
 * - オプティミスティックアップデート
 * - キャッシュ機能の実装
 */
export const useDoctorShiftList = () => {
  const [state, setState] = useState<DoctorShiftListState>({
    data: [],
    meta: null,
    isLoading: false,
    error: null,
    filters: {
      page: 1,
      page_size: 10,
      sort_by: 'shift_date',
      sort_order: 'desc'
    },
    selectedShift: null,
    editModalOpen: false,
    detailModalOpen: false,
    deleteDialogOpen: false
  });

  /**
   * 医師シフト一覧を取得
   */
  const fetchShifts = useCallback(async (filters?: DoctorShiftFilterOptions) => {
    setState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      const mergedFilters = { ...state.filters, ...filters };
      const response = await doctorShiftApi.getDoctorShifts(mergedFilters);

      // 医師IDのユニークリストを取得
      const uniqueDoctorIds = Array.from(new Set(response.data.map(shift => shift.doctor_id)));
      
      // 医師情報を取得（バックエンドの100件制限に対応）
      let doctorMap: Map<string, Doctor> = new Map();
      if (uniqueDoctorIds.length > 0) {
        try {
          let currentPage = 1;
          let hasMorePages = true;
          const maxPerPage = 100; // バックエンドの最大件数
          
          // 必要に応じて複数ページを取得
          while (hasMorePages) {
            const doctorsResponse = await doctorApiService.getDoctors({
              page: currentPage,
              per_page: maxPerPage
            });
            
            // 医師IDと医師情報のマッピングを作成
            doctorsResponse.data.forEach(doctor => {
              // doctor_codeを医師IDとして使用
              doctorMap.set(doctor.doctor_code, doctor);
            });
            
            // 次のページがあるか確認
            if (doctorsResponse.meta && doctorsResponse.meta.current_page < doctorsResponse.meta.last_page) {
              currentPage++;
            } else {
              hasMorePages = false;
            }
            
            // 必要な医師情報がすべて取得できたら終了
            const foundDoctorIds = Array.from(doctorMap.keys());
            const allDoctorsFound = uniqueDoctorIds.every(id => foundDoctorIds.includes(id));
            if (allDoctorsFound) {
              hasMorePages = false;
            }
          }
        } catch (doctorError) {
          console.error('医師情報の取得に失敗しました:', doctorError);
          // 医師情報が取得できなくても処理を続行
        }
      }

      // シフトデータに医師名を追加
      const enrichedData = response.data.map(shift => {
        const doctor = doctorMap.get(shift.doctor_id);
        if (doctor) {
          return {
            ...shift,
            doctor_first_name: doctor.first_name,
            doctor_last_name: doctor.last_name,
            doctor_full_name: `${doctor.first_name}${doctor.last_name}先生`
          };
        }
        return shift;
      });

      setState(prev => ({
        ...prev,
        data: enrichedData,
        meta: response.meta,
        isLoading: false,
        filters: mergedFilters
      }));
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'データ取得に失敗しました';
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: errorMessage
      }));
    }
  }, [state.filters]);

  /**
   * 初回データ取得
   */
  useEffect(() => {
    fetchShifts();
  }, []);

  /**
   * フィルター更新
   */
  const updateFilters = useCallback((newFilters: Partial<DoctorShiftFilterOptions>) => {
    const updatedFilters = { ...state.filters, ...newFilters };
    fetchShifts(updatedFilters);
  }, [fetchShifts]);

  /**
   * ページ変更
   */
  const handlePageChange = useCallback((page: number) => {
    updateFilters({ page });
  }, [updateFilters]);

  /**
   * ページサイズ変更
   */
  const handlePageSizeChange = useCallback((pageSize: number) => {
    updateFilters({ page: 1, page_size: pageSize });
  }, [updateFilters]);

  /**
   * ソート変更
   */
  const handleSort = useCallback((sortBy: keyof DoctorShift) => {
    const isAsc = state.filters.sort_by === sortBy && state.filters.sort_order === 'asc';
    updateFilters({
      sort_by: sortBy,
      sort_order: isAsc ? 'desc' : 'asc'
    });
  }, [state.filters.sort_by, state.filters.sort_order, updateFilters]);

  /**
   * 検索実行
   */
  const handleSearch = useCallback((search: string) => {
    updateFilters({ search, page: 1 });
  }, [updateFilters]);

  /**
   * データリフレッシュ
   */
  const refresh = useCallback(() => {
    fetchShifts(state.filters);
  }, [fetchShifts, state.filters]);

  /**
   * 医師シフト詳細表示
   */
  const showDetails = useCallback((shift: DoctorShift) => {
    setState(prev => ({
      ...prev,
      selectedShift: shift,
      detailModalOpen: true
    }));
  }, []);

  /**
   * 医師シフト編集モーダル表示
   */
  const showEditModal = useCallback((shift?: DoctorShift) => {
    setState(prev => ({
      ...prev,
      selectedShift: shift || null,
      editModalOpen: true
    }));
  }, []);

  /**
   * 医師シフト削除確認ダイアログ表示
   */
  const showDeleteDialog = useCallback((shift: DoctorShift) => {
    setState(prev => ({
      ...prev,
      selectedShift: shift,
      deleteDialogOpen: true
    }));
  }, []);

  /**
   * モーダル・ダイアログを閉じる
   */
  const closeModals = useCallback(() => {
    setState(prev => ({
      ...prev,
      selectedShift: null,
      editModalOpen: false,
      detailModalOpen: false,
      deleteDialogOpen: false
    }));
  }, []);

  /**
   * 医師シフト作成
   */
  const createShift = useCallback(async (shiftData: DoctorShiftCreateRequest): Promise<boolean> => {
    try {
      setState(prev => ({ ...prev, isLoading: true, error: null }));
      
      await doctorShiftApi.createDoctorShift(shiftData);
      
      // 作成後にデータをリフレッシュ
      await fetchShifts(state.filters);
      
      setState(prev => ({ ...prev, isLoading: false }));
      closeModals();
      
      return true;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : '作成に失敗しました';
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: errorMessage
      }));
      return false;
    }
  }, [fetchShifts, state.filters, closeModals]);

  /**
   * 医師シフト更新
   */
  const updateShift = useCallback(async (id: string, shiftData: DoctorShiftUpdateRequest): Promise<boolean> => {
    try {
      setState(prev => ({ ...prev, isLoading: true, error: null }));
      
      await doctorShiftApi.updateDoctorShift(id, shiftData);
      
      // 更新後にデータをリフレッシュ
      await fetchShifts(state.filters);
      
      setState(prev => ({ ...prev, isLoading: false }));
      closeModals();
      
      return true;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : '更新に失敗しました';
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: errorMessage
      }));
      return false;
    }
  }, [fetchShifts, state.filters, closeModals]);

  /**
   * 医師シフト削除
   */
  const deleteShift = useCallback(async (id: string): Promise<boolean> => {
    try {
      setState(prev => ({ ...prev, isLoading: true, error: null }));
      
      await doctorShiftApi.deleteDoctorShift(id);
      
      // 削除後にデータをリフレッシュ
      await fetchShifts(state.filters);
      
      setState(prev => ({ ...prev, isLoading: false }));
      closeModals();
      
      return true;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : '削除に失敗しました';
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: errorMessage
      }));
      return false;
    }
  }, [fetchShifts, state.filters, closeModals]);

  /**
   * 複数選択削除
   */
  const bulkDeleteShifts = useCallback(async (ids: string[]): Promise<boolean> => {
    try {
      setState(prev => ({ ...prev, isLoading: true, error: null }));
      
      await doctorShiftApi.bulkDeleteDoctorShifts(ids);
      
      // 削除後にデータをリフレッシュ
      await fetchShifts(state.filters);
      
      setState(prev => ({ ...prev, isLoading: false }));
      
      return true;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : '一括削除に失敗しました';
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: errorMessage
      }));
      return false;
    }
  }, [fetchShifts, state.filters]);

  /**
   * エラーをクリア
   */
  const clearError = useCallback(() => {
    setState(prev => ({ ...prev, error: null }));
  }, []);

  return {
    // 状態
    ...state,
    
    // データ操作
    fetchShifts,
    updateFilters,
    handlePageChange,
    handlePageSizeChange,
    handleSort,
    handleSearch,
    refresh,
    
    // モーダル・ダイアログ操作
    showDetails,
    showEditModal,
    showDeleteDialog,
    closeModals,
    
    // CRUD操作
    createShift,
    updateShift,
    deleteShift,
    bulkDeleteShifts,
    
    // ユーティリティ
    clearError
  };
};